import {
  IconWrapper,
  capitalizeFirstLetter,
  computeNavigationDatesForMemberInCommunity,
  ellipsizeMiddle,
  formatHumanDate,
} from '@flexo/general';
import { useOnClickOutside } from '@flexo/hooks';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import { TableFooter, TableSortLabel } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import parse from 'html-react-parser';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { TableSkeleton } from '../skeletons/table-skeleton';
import { Collapsible } from './helpers';
import { useStickyTableFooterEntry } from './sticky-table-footer';
import styles from './table.module.scss';

function getPreviousSiblingCoordinates(elementId: string): any {
  const element: any = document.getElementById(elementId);
  return element?.getBoundingClientRect();
}

function getPopperFixedStyle(anchorElementId: string): any {
  const rect: any = getPreviousSiblingCoordinates(anchorElementId);
  if (!rect) return {};

  const gap = 8;
  const minWidth = 250;
  const viewportWidth =
    window.innerWidth || document?.documentElement?.clientWidth || 0;
  const viewportHeight =
    window.innerHeight || document?.documentElement?.clientHeight || 0;

  const spaceBelow = viewportHeight - rect.bottom;

  const style: any = {};
  if (spaceBelow >= 200) {
    style.top = rect.bottom + gap;
  } else {
    style.bottom = viewportHeight - rect.top + gap;
  }

  const tentativeLeft = rect.left;
  const clampedLeft = Math.max(
    8,
    Math.min(tentativeLeft, viewportWidth - minWidth - 8)
  );
  style.left = clampedLeft;

  return style;
}

export function MembersTable(props: any) {
  const metadata = useSelector((state: any) => state.metadata);
  const { t } = useTranslation();
  const params = useParams();
  const LOADING_STATE = metadata?.SetMembersAsyncStatus;

  // Community context: when on /community/:id, compute date params for member navigation
  const communityId = params.id;
  const entitiesMembers = (metadata?.entities as any)?.member || [];
  // Include previous/future members so navigation works for past members too
  const categorizedMembers = [
    ...(metadata?.member?.current || []),
    ...(metadata?.member?.previous || []),
    ...(metadata?.member?.future || []),
  ];
  const allMembersMap = new Map();
  entitiesMembers.forEach((m: any) => allMembersMap.set(m.memberId, m));
  categorizedMembers.forEach((m: any) => {
    if (!allMembersMap.has(m.memberId)) allMembersMap.set(m.memberId, m);
  });

  function getMemberNavigationQueryParams(memberId: string): string | undefined {
    // Only meaningful when navigating from a community context (/community/:id).
    if (!communityId) return undefined;

    const urlParams = new URLSearchParams(window?.location?.search || '');
    const hasDates =
      urlParams.has('dateFrom') &&
      urlParams.has('dateTo') &&
      urlParams.has('timeSet');

    // Dismissed case: if there is no date context yet, inject the member's last
    // period in this community so the (changelog) date resolution stays anchored
    // to it. If dates are already present, preserve them.
    if (!hasDates) {
      const member = allMembersMap.get(memberId);
      const dates = computeNavigationDatesForMemberInCommunity(member, communityId);
      if (dates) {
        urlParams.set('dateFrom', dates.dateFrom);
        urlParams.set('dateTo', dates.dateTo);
        urlParams.set('timeSet', dates.timeSet);
      }
    }

    // FLEXO-6114: always carry the community-of-origin so the member page keeps
    // the community the user actually clicked from (esp. multi-community members
    // where no date can disambiguate). Consumed once, then stripped from the URL.
    urlParams.set('fromCommunity', communityId);

    const q = urlParams.toString();
    return q ? `?${q}` : undefined;
  }

  const futureColumns: any | null =
    t(`widgets.tables.FutureMemebersTable`, { returnObjects: true }) || null;
  const currentColumns: any | null =
    t(`widgets.tables.CurrentMemebersTable`, { returnObjects: true }) || null;
  const previousColumns: any | null =
    t(`widgets.tables.PreviousMemebersTable`, { returnObjects: true }) || null;

  const [futureRows, setFutureRows] = useState<Array<any>>([]);
  const [futureRowsSitesNumber, setFutureRowsSitesNumber] = useState<number>(0);
  const [currentRows, setCurrentRows] = useState<Array<any>>([]);
  const [currentRowsSitesNumber, setCurrentRowsSitesNumber] =
    useState<number>(0);
  const [previousRows, setPreviousRows] = useState<Array<any>>([]);
  const [previousRowsSitesNumber, setPreviousRowsSitesNumber] =
    useState<number>(0);

  const [orderFuture, setOrderFuture] = useState(['name', 'asc']);
  const [orderCurrent, setOrderCurrent] = useState(['name', 'asc']);
  const [orderPrevious, setOrderPrevious] = useState(['name', 'asc']);

  const [futureOpen, setFutureOpen] = useState<string | null>(null);
  const [currentOpen, setCurrentOpen] = useState<string | null>(null);
  const [previousOpen, setPreviousOpen] = useState<string | null>(null);

  const [futurePopperStyle, setFuturePopperStyle] = useState<any>(null);
  const [currentPopperStyle, setCurrentPopperStyle] = useState<any>(null);
  const [previousPopperStyle, setPreviousPopperStyle] = useState<any>(null);

  const [futureExpanded, setFutureExpanded] = useState(true);
  const [currentExpanded, setCurrentExpanded] = useState(true);
  const [previousExpanded, setPreviousExpanded] = useState(true);

  // Refs for JS-driven sticky (CSS sticky breaks inside Ionic IonContent).
  const futureSectionRef = useRef<HTMLDivElement>(null);
  const futureTitleRef = useRef<HTMLDivElement>(null);
  const futureTitlePlaceholderRef = useRef<HTMLDivElement>(null);
  const futureHeaderRef = useRef<HTMLDivElement>(null);
  const futureHeaderPlaceholderRef = useRef<HTMLDivElement>(null);
  const futureViewportRef = useRef<HTMLDivElement>(null);
  const futureBodyContainerRef = useRef<HTMLDivElement>(null);

  const currentSectionRef = useRef<HTMLDivElement>(null);
  const currentTitleRef = useRef<HTMLDivElement>(null);
  const currentTitlePlaceholderRef = useRef<HTMLDivElement>(null);
  const currentHeaderRef = useRef<HTMLDivElement>(null);
  const currentHeaderPlaceholderRef = useRef<HTMLDivElement>(null);
  const currentViewportRef = useRef<HTMLDivElement>(null);
  const currentBodyContainerRef = useRef<HTMLDivElement>(null);

  const previousSectionRef = useRef<HTMLDivElement>(null);
  const previousTitleRef = useRef<HTMLDivElement>(null);
  const previousTitlePlaceholderRef = useRef<HTMLDivElement>(null);
  const previousHeaderRef = useRef<HTMLDivElement>(null);
  const previousHeaderPlaceholderRef = useRef<HTMLDivElement>(null);
  const previousViewportRef = useRef<HTMLDivElement>(null);
  const previousBodyContainerRef = useRef<HTMLDivElement>(null);

  const futureExpandedRef = useRef(futureExpanded);
  const currentExpandedRef = useRef(currentExpanded);
  const previousExpandedRef = useRef(previousExpanded);
  futureExpandedRef.current = futureExpanded;
  currentExpandedRef.current = currentExpanded;
  previousExpandedRef.current = previousExpanded;

  const futureAnchorEl = useRef<any>(null);
  const currentAnchorEl = useRef<any>(null);
  const previousAnchorEl = useRef<any>(null);

  // JS-driven sticky: per frame, for each section, fix the collapse title at
  // the scroll-container top and stack the mini-header below it. All values
  // (scroll baseline, title height, header height, section rect) are measured
  // each frame — no hardcoded thresholds. Handles responsive reflow and Ionic
  // shadow-DOM scroll container.
  useEffect(() => {
    let rafId: number | null = null;
    let scrollContainer: HTMLElement | null = null;
    let fixedHeaderBottom = 0;

    const findScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;
      // Ionic case: traverse up to ion-content, peek into its shadow root
      const ionContent = el.closest('ion-content') as any;
      if (ionContent && ionContent.shadowRoot) {
        const innerScroll = ionContent.shadowRoot.querySelector('.inner-scroll');
        if (innerScroll) return innerScroll as HTMLElement;
      }
      // Generic: first ancestor with overflow-y auto|scroll
      let p: HTMLElement | null = el.parentElement;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if (oy === 'auto' || oy === 'scroll') return p;
        p = p.parentElement;
      }
      return null;
    };

    // Walk up from the anchor, at each level scanning preceding siblings
    // (and their descendants) for any element with `position: fixed` anchored
    // near the viewport top — its bottom edge is where "free scrollable space"
    // actually starts, so our sticky must anchor there. All measured live.
    const measureFixedHeaderBottom = (anchor: HTMLElement): number => {
      let maxBottom = 0;
      const check = (el: Element) => {
        const cs = window.getComputedStyle(el as HTMLElement);
        if (cs.position !== 'fixed') return;
        const r = (el as HTMLElement).getBoundingClientRect();
        if (r.top <= 2 && r.height > 0 && r.bottom > maxBottom) {
          maxBottom = r.bottom;
        }
      };
      let node: HTMLElement | null = anchor;
      while (node) {
        let sib = node.previousElementSibling;
        while (sib) {
          check(sib);
          sib.querySelectorAll('*').forEach(check);
          sib = sib.previousElementSibling;
        }
        node = node.parentElement;
      }
      return maxBottom;
    };

    const refreshFixedHeader = () => {
      const anyEl =
        futureTitleRef.current ||
        currentTitleRef.current ||
        previousTitleRef.current;
      if (anyEl) fixedHeaderBottom = measureFixedHeaderBottom(anyEl);
    };

    window.addEventListener('resize', refreshFixedHeader);
    // Recompute periodically in case fixed headers mount/unmount during session
    const refreshTimer = window.setInterval(refreshFixedHeader, 1000);

    type SectionHandles = {
      sectionRef: React.RefObject<HTMLDivElement>;
      titleRef: React.RefObject<HTMLDivElement>;
      titlePhRef: React.RefObject<HTMLDivElement>;
      headerRef: React.RefObject<HTMLDivElement>;
      headerPhRef: React.RefObject<HTMLDivElement>;
      expandedRef: React.MutableRefObject<boolean>;
    };

    const sections: SectionHandles[] = [
      {
        sectionRef: futureSectionRef,
        titleRef: futureTitleRef,
        titlePhRef: futureTitlePlaceholderRef,
        headerRef: futureHeaderRef,
        headerPhRef: futureHeaderPlaceholderRef,
        expandedRef: futureExpandedRef,
      },
      {
        sectionRef: currentSectionRef,
        titleRef: currentTitleRef,
        titlePhRef: currentTitlePlaceholderRef,
        headerRef: currentHeaderRef,
        headerPhRef: currentHeaderPlaceholderRef,
        expandedRef: currentExpandedRef,
      },
      {
        sectionRef: previousSectionRef,
        titleRef: previousTitleRef,
        titlePhRef: previousTitlePlaceholderRef,
        headerRef: previousHeaderRef,
        headerPhRef: previousHeaderPlaceholderRef,
        expandedRef: previousExpandedRef,
      },
    ];

    const unstick = (
      el: HTMLElement | null,
      ph: HTMLElement | null
    ) => {
      if (el) {
        el.style.position = '';
        el.style.top = '';
        el.style.left = '';
        el.style.width = '';
        el.style.zIndex = '';
        el.style.clipPath = '';
      }
      if (ph) ph.style.height = '0px';
    };

    const update = () => {
      if (!scrollContainer) {
        const anyEl =
          futureTitleRef.current ||
          currentTitleRef.current ||
          previousTitleRef.current;
        if (anyEl) {
          scrollContainer = findScrollContainer(anyEl);
          if (!fixedHeaderBottom) fixedHeaderBottom = measureFixedHeaderBottom(anyEl);
        }
      }
      const scrollTop = scrollContainer
        ? scrollContainer.getBoundingClientRect().top
        : 0;
      // Baseline = whichever is lower in viewport: scroll-container top or
      // the bottom of any fixed header above us. Sticky anchors there.
      const baseline = Math.max(scrollTop, fixedHeaderBottom);

      for (const s of sections) {
        const section = s.sectionRef.current;
        const title = s.titleRef.current;
        const titlePh = s.titlePhRef.current;
        const header = s.headerRef.current;
        const headerPh = s.headerPhRef.current;
        if (!section || !title || !titlePh) continue;

        const sectionRect = section.getBoundingClientRect();
        const titlePhRect = titlePh.getBoundingClientRect();

        // Title sticky range: placeholder has scrolled past baseline AND
        // section bottom still has room for at least the title.
        const titleHeight = title.offsetHeight;
        const titleShouldStick =
          titlePhRect.top <= baseline &&
          sectionRect.bottom > baseline + titleHeight;

        // Diagnostic: expose live sticky state via data-* for DevTools
        title.setAttribute('data-sticky-baseline', String(Math.round(baseline)));
        title.setAttribute('data-sticky-fixedbottom', String(Math.round(fixedHeaderBottom)));
        title.setAttribute('data-sticky-phtop', String(Math.round(titlePhRect.top)));
        title.setAttribute('data-sticky-should', String(titleShouldStick));

        if (titleShouldStick) {
          titlePh.style.height = `${titleHeight}px`;
          title.style.position = 'fixed';
          title.style.top = `${baseline}px`;
          title.style.left = `${titlePhRect.left}px`;
          title.style.width = `${titlePhRect.width}px`;
          title.style.zIndex = '12';
        } else {
          unstick(title, titlePh);
        }

        // Header mini-table only exists when section is expanded.
        if (!header || !headerPh || !s.expandedRef.current) {
          if (header) unstick(header, headerPh);
          continue;
        }

        const headerPhRect = headerPh.getBoundingClientRect();
        const stuckTitleOffset = titleShouldStick ? titleHeight : 0;
        const headerTop = baseline + stuckTitleOffset;
        const headerHeight = header.offsetHeight;
        const headerShouldStick =
          headerPhRect.top <= headerTop &&
          sectionRect.bottom > headerTop + headerHeight;

        if (headerShouldStick) {
          // Measure from the body table before promoting the header to `fixed`.
          // Once fixed, `width: 100%` starts resolving against the viewport and
          // the sticky header becomes wider than the scrollable tbody.
          const bodyContainer = header.nextElementSibling as HTMLElement | null;
          const bodyTable = bodyContainer?.querySelector('table') as HTMLElement | null;
          const scrollViewport =
            (header.parentElement?.parentElement as HTMLElement | null) || section;
          const scrollViewportRect = scrollViewport.getBoundingClientRect();
          const stickyWidth =
            bodyTable?.offsetWidth || header.offsetWidth || headerPhRect.width;

          headerPh.style.height = `${headerHeight}px`;
          header.style.position = 'fixed';
          header.style.top = `${headerTop}px`;
          header.style.left = `${headerPhRect.left}px`;
          header.style.width = `${stickyWidth}px`;
          header.style.zIndex = '11';

          // The sticky header must show the same horizontal slice as the body:
          // clip it to the visible viewport of the Collapsible, while its left
          // offset keeps following horizontal scroll via headerPhRect.left.
          const hLeft = headerPhRect.left;
          const hRight = hLeft + stickyWidth;
          const clipLeft = Math.max(0, scrollViewportRect.left - hLeft);
          const clipRight = Math.max(0, hRight - scrollViewportRect.right);
          header.style.clipPath = `inset(0 ${clipRight}px 0 ${clipLeft}px)`;
        } else {
          unstick(header, headerPh);
        }
      }
    };

    const poll = () => {
      update();
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.clearInterval(refreshTimer);
      window.removeEventListener('resize', refreshFixedHeader);
      for (const s of sections) {
        unstick(s.titleRef.current, s.titlePhRef.current);
        unstick(s.headerRef.current, s.headerPhRef.current);
      }
    };
  }, []);

  useOnClickOutside(
    futureAnchorEl,
    () => (setFutureOpen(null), setFuturePopperStyle(null))
  );
  useOnClickOutside(
    currentAnchorEl,
    () => (setCurrentOpen(null), setCurrentPopperStyle(null))
  );
  useOnClickOutside(
    previousAnchorEl,
    () => (setPreviousOpen(null), setPreviousPopperStyle(null))
  );

  const handleClick = (event: any, id: string, context: string) => {
    if (id === futureOpen || id === currentOpen || id === previousOpen) {
      return;
    }

    switch (context) {
      case 'future':
        if (futureOpen === id) {
          setFutureOpen(null);
          setFuturePopperStyle(null);
        } else {
          setFutureOpen(id);
          setFuturePopperStyle(
            getPopperFixedStyle(`future__popper__${id || 'missing_id'}`)
          );
        }
        break;
      case 'current':
        if (currentOpen === id) {
          setCurrentOpen(null);
          setCurrentPopperStyle(null);
        } else {
          setCurrentOpen(id);
          setCurrentPopperStyle(
            getPopperFixedStyle(`current__popper__${id || 'missing_id'}`)
          );
        }
        break;
      case 'previous':
        if (previousOpen === id) {
          setPreviousOpen(null);
          setPreviousPopperStyle(null);
        } else {
          setPreviousOpen(id);
          setPreviousPopperStyle(
            getPopperFixedStyle(`previous__popper__${id || 'missing_id'}`)
          );
        }
        break;
      default:
        break;
    }
  };

  const [loading, setLoading] = useState(false);

  function loadDataSources() {
    // setLoading(true);

    const sourceStore = metadata?.filteredMembers
      ? metadata?.filteredMembers
      : metadata?.member;

    const futureDataSource = [...(sourceStore?.future || [])].sort((a, b) =>
      `${a.memberFirstName} ${a.memberLastName}` >
      `${b.memberFirstName} ${b.memberLastName}`
        ? 1
        : -1
    );
    const currentDataSource = [...(sourceStore?.current || [])].sort((a, b) =>
      `${a.memberFirstName} ${a.memberLastName}` >
      `${b.memberFirstName} ${b.memberLastName}`
        ? 1
        : -1
    );
    const previousDataSource = [...(sourceStore?.previous || [])].sort((a, b) =>
      `${a.memberFirstName} ${a.memberLastName}` >
      `${b.memberFirstName} ${b.memberLastName}`
        ? 1
        : -1
    );

    if (!_.isEqual(futureDataSource, futureRows)) {
      const _futureRowsSitesNumber = futureDataSource.reduce(
        (prev, next) => prev + (next?.sites?.length || 0),
        0
      );
      setFutureRows(futureDataSource);
      setFutureRowsSitesNumber(_futureRowsSitesNumber);
    }
    if (!_.isEqual(currentDataSource, currentRows)) {
      const _currentRowsSitesNumber = currentDataSource.reduce(
        (prev, next) => prev + (next?.sites?.length || 0),
        0
      );
      setCurrentRows(currentDataSource);
      setCurrentRowsSitesNumber(_currentRowsSitesNumber);
    }
    if (!_.isEqual(previousDataSource, previousRows)) {
      const _previousRowsSitesNumber = previousDataSource.reduce(
        (prev, next) => prev + (next?.sites?.length || 0),
        0
      );
      setPreviousRows(previousDataSource);
      setPreviousRowsSitesNumber(_previousRowsSitesNumber);
    }

    setTimeout(() => {
      setLoading(false);
    }, 1);
  }

  useEffect(() => {
    loadDataSources();
  }, [metadata?.filteredMembers, metadata?.members]);

  useEffect(() => {
    loadDataSources();
  }, []);

  function sortFutureBy(columnId: string) {
    try {
      let _midArray = [...futureRows];
      let direction = 'asc';

      if (orderFuture[0] === columnId && orderFuture[1] === 'asc') {
        direction = 'desc';
      }

      switch (columnId) {
        case 'name':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a.memberFirstName} ${a.memberLastName}` >
                  `${b.memberFirstName} ${b.memberLastName}`
                : `${a.memberFirstName} ${a.memberLastName}` <
                  `${b.memberFirstName} ${b.memberLastName}`
            )
              ? 1
              : -1
          );
          break;
        case 'pod':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a?.sites?.[0]?.pod}` > `${b?.sites?.[0]?.pod} `
                : `${a?.sites?.[0]?.pod}` < `${b?.sites?.[0]?.pod}`
            )
              ? 1
              : -1
          );
          break;
        case 'join_date':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a.joinDate}` > `${b.joinDate}`
                : `${a.joinDate}` < `${b.joinDate}`
            )
              ? 1
              : -1
          );
          break;
      }

      setOrderFuture([columnId, direction]);
      setFutureRows(_midArray);
    } catch (err: any) {
      // console.warn(err)
    }
  }

  function sortCurrentBy(columnId: string) {
    function getNames(a: any, b: any) {
      const a_name = `${a.memberFirstName} ${a.memberLastName}`;
      const b_name = `${b.memberFirstName} ${b.memberLastName}`;
      return [a_name, b_name];
    }

    try {
      let _midArray = [...currentRows];
      let direction = 'asc';

      if (orderCurrent[0] === columnId && orderCurrent[1] === 'asc') {
        direction = 'desc';
      }

      switch (columnId) {
        case 'name':
          _midArray = _midArray.sort((a, b) => {
            const [a_name, b_name] = getNames(a, b);
            return (direction === 'asc' ? a_name > b_name : a_name < b_name)
              ? 1
              : -1;
          });

          break;
        case 'pod':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a?.sites?.[0]?.pod}` > `${b?.sites?.[0]?.pod} `
                : `${a?.sites?.[0]?.pod}` < `${b?.sites?.[0]?.pod}`
            )
              ? 1
              : -1
          );
          break;
        case 'join_date':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a.joinDate}` > `${b.joinDate}`
                : `${a.joinDate}` < `${b.joinDate}`
            )
              ? 1
              : -1
          );
          break;
      }

      setOrderCurrent([columnId, direction]);
      setCurrentRows(_midArray);
    } catch (err: any) {
      // console.warn(err)
    }
  }

  function sortPreviousBy(columnId: string) {
    try {
      let _midArray = [...previousRows];
      let direction = 'asc';

      if (orderPrevious[0] === columnId && orderPrevious[1] === 'asc') {
        direction = 'desc';
      }

      switch (columnId) {
        case 'name':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a.memberFirstName} ${a.memberLastName}` >
                  `${b.memberFirstName} ${b.memberLastName}`
                : `${a.memberFirstName} ${a.memberLastName}` <
                  `${b.memberFirstName} ${b.memberLastName}`
            )
              ? 1
              : -1
          );
          break;
        case 'pod':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a?.sites?.[0]?.pod}` > `${b?.sites?.[0]?.pod} `
                : `${a?.sites?.[0]?.pod}` < `${b?.sites?.[0]?.pod}`
            )
              ? 1
              : -1
          );
          break;
        case 'join_date':
          _midArray = _midArray.sort((a, b) =>
            (
              direction === 'asc'
                ? `${a.joinDate}` > `${b.joinDate}`
                : `${a.joinDate}` < `${b.joinDate}`
            )
              ? 1
              : -1
          );
          break;
      }

      setOrderPrevious([columnId, direction]);
      setPreviousRows(_midArray);
    } catch (err: any) {
      // console.warn(err)
    }
  }

  const visibleSectionsCount = [
    futureRows?.length > 0,
    currentRows?.length > 0,
    previousRows?.length > 0,
  ].filter(Boolean).length;
  const allowCollapse = visibleSectionsCount > 1;

  const renderMembersFooterIcon = (isActive: boolean, direction: string) =>
    isActive ? (
      direction === 'asc' ? (
        <IconWrapper
          iconName="chevron-up"
          className={`${styles.Table__TH__Icon}`}
        />
      ) : (
        <IconWrapper
          iconName="chevron-down"
          className={`${styles.Table__TH__Icon}`}
        />
      )
    ) : (
      <div className={styles.Table__Members__FooterIconSpacer}></div>
    );

  const renderMembersColGroup = () => (
    <colgroup>
      <col className={styles.Table__Members__Col__Primary} />
      <col className={styles.Table__Members__Col__Secondary} />
      <col className={styles.Table__Members__Col__Tertiary} />
    </colgroup>
  );

  const renderMembersFooterTable = ({
    membersCount,
    sitesCount,
    order,
    onSortName,
    onSortSites,
    sortable,
    sitesCellIndex,
    tableLabel,
  }: {
    membersCount: number;
    sitesCount: number;
    order: string[];
    onSortName: () => void;
    onSortSites: () => void;
    sortable: boolean;
    sitesCellIndex: 1 | 2;
    tableLabel: string;
  }) => {
    const nameCell = (
      <TableCell
        align="left"
        className={`${sortable && styles.Table__Footer__Cell__Clickable}`}
      >
        <div
          onClick={() => (sortable ? onSortName() : null)}
          className={`${
            sortable && order[0] === 'name'
              ? styles.Table__Footer__Cell__Active
              : styles.Table__Footer__Cell
          }`}
        >
          <span>
            {parse(
              t(
                `widgets.tables.utils.${
                  membersCount === 1 ? 'member' : 'members'
                }`,
                { members: membersCount }
              )
            )}
          </span>
          {renderMembersFooterIcon(order[0] === 'name', order[1])}
        </div>
      </TableCell>
    );

    const sitesCell = (
      <TableCell
        align="left"
        className={`${sortable && styles.Table__Footer__Cell__Clickable}`}
      >
        <div
          onClick={() => (sortable ? onSortSites() : null)}
          className={`${
            sortable && order[0] === 'pod'
              ? styles.Table__Footer__Cell__Active
              : styles.Table__Footer__Cell
          }`}
        >
          <span>
            {parse(
              t(
                `widgets.tables.utils.${sitesCount === 1 ? 'site' : 'sites'}`,
                { sites: sitesCount }
              )
            )}
          </span>
          {renderMembersFooterIcon(order[0] === 'pod', order[1])}
        </div>
      </TableCell>
    );

    const emptyCell = <TableCell align="left"></TableCell>;

    return (
      <TableContainer
        component={Paper}
        className={styles.Table__Members__StickyFooterContainer}
      >
        <Table
          aria-label={tableLabel}
          className={styles.Table__Members__StickyFooterTable}
        >
          {renderMembersColGroup()}
          <TableFooter>
            <TableRow>
              {nameCell}
              {sitesCellIndex === 1 ? sitesCell : emptyCell}
              {sitesCellIndex === 2 ? sitesCell : emptyCell}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    );
  };

  const futureStickyFooter = useMemo(
    () => () =>
      renderMembersFooterTable({
        membersCount: futureRows?.length || 0,
        sitesCount: futureRowsSitesNumber,
        order: orderFuture,
        onSortName: () => sortFutureBy('name'),
        onSortSites: () => sortFutureBy('pod'),
        sortable: !!futureColumns.rows?.[0]?.sortable,
        sitesCellIndex: 1,
        tableLabel: 'future members sticky footer',
      }),
    [
      futureColumns.rows,
      futureRows?.length,
      futureRowsSitesNumber,
      orderFuture,
    ]
  );

  const currentStickyFooter = useMemo(
    () => () =>
      renderMembersFooterTable({
        membersCount: currentRows?.length || 0,
        sitesCount: currentRowsSitesNumber,
        order: orderCurrent,
        onSortName: () => sortCurrentBy('name'),
        onSortSites: () => sortCurrentBy('pod'),
        sortable: !!currentColumns.rows?.[0]?.sortable,
        sitesCellIndex: 1,
        tableLabel: 'current members sticky footer',
      }),
    [
      currentColumns.rows,
      currentRows?.length,
      currentRowsSitesNumber,
      orderCurrent,
    ]
  );

  const previousStickyFooter = useMemo(
    () => () =>
      renderMembersFooterTable({
        membersCount: previousRows?.length || 0,
        sitesCount: previousRowsSitesNumber,
        order: orderPrevious,
        onSortName: () => sortPreviousBy('name'),
        onSortSites: () => sortPreviousBy('pod'),
        sortable: !!previousColumns.rows?.[0]?.sortable,
        sitesCellIndex: 1,
        tableLabel: 'previous members sticky footer',
      }),
    [
      previousColumns.rows,
      previousRows?.length,
      previousRowsSitesNumber,
      orderPrevious,
    ]
  );

  const hasFutureStickyFooterHost = useStickyTableFooterEntry(
    useMemo(
      () =>
        futureRows?.length > 0
          ? {
              id: 'members-future',
              enabled: !allowCollapse || futureExpanded,
              sectionRef: futureSectionRef,
              focusRef: futureTitlePlaceholderRef,
              viewportRef: futureViewportRef,
              contentRef: futureBodyContainerRef,
              activationRowsPassed: 2,
              renderFooter: futureStickyFooter,
            }
          : null,
      [allowCollapse, futureExpanded, futureRows?.length, futureStickyFooter]
    )
  );

  const hasCurrentStickyFooterHost = useStickyTableFooterEntry(
    useMemo(
      () =>
        currentRows?.length > 0
          ? {
              id: 'members-current',
              enabled: !allowCollapse || currentExpanded,
              sectionRef: currentSectionRef,
              focusRef: currentTitlePlaceholderRef,
              viewportRef: currentViewportRef,
              contentRef: currentBodyContainerRef,
              activationRowsPassed: 2,
              renderFooter: currentStickyFooter,
            }
          : null,
      [allowCollapse, currentExpanded, currentRows?.length, currentStickyFooter]
    )
  );

  const hasPreviousStickyFooterHost = useStickyTableFooterEntry(
    useMemo(
      () =>
        previousRows?.length > 0
          ? {
              id: 'members-previous',
              enabled: !allowCollapse || previousExpanded,
              sectionRef: previousSectionRef,
              focusRef: previousTitlePlaceholderRef,
              viewportRef: previousViewportRef,
              contentRef: previousBodyContainerRef,
              activationRowsPassed: 2,
              renderFooter: previousStickyFooter,
            }
          : null,
      [
        allowCollapse,
        previousExpanded,
        previousRows?.length,
        previousStickyFooter,
      ]
    )
  );

  return LOADING_STATE === 'PENDING' ||
    (currentRows?.length === 0 &&
      futureRows?.length === 0 &&
      previousRows?.length === 0) ? (
    <TableSkeleton
      rows={currentColumns.rows}
      title={t('widgets.tables.members.title')}
      context="members"
      hasShimmer={LOADING_STATE === 'PENDING'}
    />
  ) : (
    <div className={`${styles.Table__Members__Wrap}`}>
      {
        futureRows && futureRows?.length > 0 ? (
          <div
            ref={futureSectionRef}
            className={`${styles.Table} ${styles.Table__Members} mb-lg-3`}
            key="future-members-section"
          >
            <div ref={futureTitlePlaceholderRef} />
            {futureColumns && (
              <div
                ref={futureTitleRef}
                className={`${styles.Table__Members__CollapseHeader} ${!allowCollapse ? styles.Table__Members__CollapseHeader__Static : ''}`}
                onClick={() => allowCollapse && setFutureExpanded((prev) => !prev)}
              >
                <span>{futureColumns.title}</span>
                {allowCollapse && (
                  <IconWrapper
                    iconName="chevron-down"
                    className={`${styles.Table__Members__Chevron} ${futureExpanded ? styles.Table__Members__Chevron__Collapsed : ''}`}
                  />
                )}
              </div>
            )}

            <Collapsible
              open={!allowCollapse || futureExpanded}
              className={styles.Table__Members__CollapsibleOverride}
              outerRef={futureViewportRef}
            >
            <div ref={futureHeaderPlaceholderRef} />
            <div
              ref={futureHeaderRef}
              className={styles.Table__Members__StickyHeaderWrapper}
            >
              <TableContainer component={Paper} sx={{ overflow: 'visible' }}>
                <Table aria-label="future members header">
                  {renderMembersColGroup()}
                  <TableHead>
                    <TableRow>
                      {futureColumns &&
                        futureColumns.rows.map(
                          (column: any, column_i: number, column_arr: any) => (
                            <TableCell
                              {...(column_arr?.length - 1 === column_i
                                ? { align: 'right' }
                                : {})}
                              className={`
                        ${
                          column.sortable
                            ? styles.Table__Header__Cell__Sortable
                            : styles.Table__Header__Cell__Static
                        }
                        ${
                          column.sortable && orderFuture[0] === column.field
                            ? styles.Table__Header__Cell__Active
                            : styles.Table__Header__Cell
                        } `}
                              key={`future_members_head_col_${column_i}_${column.field}`}
                            >
                              <TableSortLabel
                                active={
                                  column.sortable &&
                                  orderFuture[0] === column.field
                                }
                                direction={
                                  orderFuture[0] === column.field &&
                                  orderFuture[1] === 'asc'
                                    ? 'asc'
                                    : 'desc'
                                }
                                onClick={() =>
                                  column.sortable
                                    ? sortFutureBy(column.field)
                                    : null
                                }
                                className={`${styles.Table__Header__Cell} `}
                                IconComponent={() =>
                                  orderFuture[0] === column.field ? (
                                    orderFuture[1] === 'asc' ? (
                                      <IconWrapper
                                        iconName="chevron-up"
                                        className={`${styles.Table__TH__Icon}`}
                                      />
                                    ) : (
                                      <IconWrapper
                                        iconName="chevron-down"
                                        className={`${styles.Table__TH__Icon}`}
                                      />
                                    )
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <div> {column.title}</div>
                              </TableSortLabel>
                            </TableCell>
                          )
                        )}
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            </div>
            <TableContainer
              ref={futureBodyContainerRef}
              key="future-table-container"
              component={Paper}
              className={`${futureColumns.title ? 'mt-1' : ''}`}
              sx={{ overflow: 'visible' }}
            >
              <Table aria-label="simple table">
                {renderMembersColGroup()}
                <TableBody key="future-table-body">
                  {(futureRows || []).map((row) => (
                    <TableRow
                      key={`future_member_row_${row.memberId}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      style={{ height: 100 }}
                      className={`${styles.Table__Body__Row__Clickable}`}
                      onClick={() =>
                        (window.getSelection()?.toString() || '')?.length < 4
                          ? navigateToMember('member', row?.memberId, getMemberNavigationQueryParams(row?.memberId))
                          : null
                      }
                    >
                      <TableCell
                        className={`${
                          !futureColumns?.rows?.[0]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`future_cell_name_${row.memberId}`}
                        component="th"
                        scope="row"
                      >
                        <div className={`${styles.Table__Cell__Member__Name}`}>
                          <span>
                            {capitalizeFirstLetter(
                              ellipsizeMiddle(
                                `${row?.memberFirstName || ''} ${
                                  row?.memberLastName || ''
                                }`,
                                [
                                  30, 20, 20
                                ]
                              )
                            )}
                          </span>
                          <label className={styles.Table__Cell__Member__ID}>
                            {row?.memberExternalId || 'missing_member_id'}{' '}
                          </label>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !futureColumns?.rows?.[1]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`future_cell_pod_${row.memberId}`}
                        component="th"
                        scope="row"
                      >
                        <div>
                          <label
                            className={styles.Table__Cell__Member__Site__Label}
                          >
                            {row?.sites?.[0]?.pod || 'missing_pod'}
                            {/* {ellipsizeMiddle(

                            )}{' '} */}
                          </label>

                          <div
                            className={styles.Table__Cell__Member__Sites__Names}
                          >
                            {capitalizeFirstLetter(
                              row?.sites?.[0]?.siteName || 'missing name'
                            )}
                            <span
                              id={`future__popper__${
                                row?.sites?.[0]?.siteId || 'missing_id'
                              }`}
                              className={styles.Popper__Label}
                              onMouseEnter={(event: any) =>
                                handleClick(
                                  event,
                                  row?.sites?.[0]?.siteId || 'missing_id',
                                  'future'
                                )
                              }
                            >
                              {' '}
                              {row.sites.length > 1 &&
                                ` + ${row.sites.length - 1}`}
                            </span>
                            {futureOpen ===
                              (row?.sites?.[0]?.siteId || 'missing_id') &&
                              (typeof document !== 'undefined'
                                ? createPortal(
                                    <div
                                      ref={futureAnchorEl}
                                      className={`${styles.Popper__Wrapper}`}
                                      onMouseLeave={() => setFutureOpen(null)}
                                      key={`future_popper_${row.memberId}`}
                                      style={
                                        futurePopperStyle ||
                                        getPopperFixedStyle(
                                          `future__popper__${
                                            row?.sites?.[0]?.siteId ||
                                            'missing_id'
                                          }`
                                        )
                                      }
                                    >
                                      {row.sites.map((memberSite: any) => {
                                        return (
                                          <div
                                            key={`future_popper_site_${memberSite.siteId}`}
                                            className={`${styles.Popper__Wrapper__Item}`}
                                            onClick={(ev: any) => (
                                              ev.preventDefault(),
                                              ev.stopPropagation(),
                                              navigateToSite(
                                                'site',
                                                memberSite?.siteId
                                              )
                                            )}
                                          >
                                            <p
                                              className={`${styles.Popper__Name}`}
                                            >
                                              {capitalizeFirstLetter(
                                                memberSite.siteName
                                              )}
                                            </p>
                                            <p
                                              className={`${styles.Popper__Text}`}
                                            >
                                              {memberSite.pod}
                                            </p>
                                          </div>
                                        );
                                      })}
                                    </div>,
                                    document.body
                                  )
                                : null)}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !futureColumns?.rows?.[2]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`future_cell_date_${row.memberId}`}
                        component="th"
                        scope="row"
                        align="right"
                      >
                        <div>
                          <label style={{ alignSelf: 'flex-end' }}>
                            {formatHumanDate(row?.sites?.[0]?.startDate || '')}
                          </label>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {!hasFutureStickyFooterHost ? (
                  <TableFooter>
                    <TableRow>
                      <TableCell
                        align="left"
                        className={`${
                          futureColumns.rows[0].sortable &&
                          styles.Table__Footer__Cell__Clickable
                        }`}
                      >
                        <div
                          onClick={() =>
                            futureColumns.rows[0].sortable
                              ? sortFutureBy('name')
                              : null
                          }
                          className={`${
                            futureColumns.rows[0].sortable &&
                            orderFuture[0] === 'name'
                              ? styles.Table__Footer__Cell__Active
                              : styles.Table__Footer__Cell
                          }`}
                        >
                          <span>
                            {parse(
                              t(
                                `widgets.tables.utils.${
                                  (futureRows?.length || 0) === 1
                                    ? 'member'
                                    : 'members'
                                }`,
                                { members: futureRows?.length || 0 }
                              )
                            )}
                          </span>

                          {orderFuture[0] === 'name' ? (
                            orderFuture[1] === 'asc' ? (
                              <IconWrapper
                                iconName="chevron-up"
                                className={`${styles.Table__TH__Icon}`}
                              />
                            ) : (
                              <IconWrapper
                                iconName="chevron-down"
                                className={`${styles.Table__TH__Icon}`}
                              />
                            )
                          ) : (
                            <div style={{ height: 32, width: 19 }}></div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell
                        align="left"
                        className={`${
                          futureColumns.rows[0].sortable &&
                          styles.Table__Footer__Cell__Clickable
                        }`}
                      >
                        <div
                          onClick={() =>
                            futureColumns.rows[0].sortable
                              ? sortFutureBy('pod')
                              : null
                          }
                          className={`${
                            futureColumns.rows[0].sortable &&
                            orderFuture[0] === 'pod'
                              ? styles.Table__Footer__Cell__Active
                              : styles.Table__Footer__Cell
                          }`}
                        >
                          <span>
                            {parse(
                              t(
                                `widgets.tables.utils.${
                                  futureRowsSitesNumber === 1
                                    ? 'site'
                                    : 'sites'
                                }`,
                                { sites: futureRowsSitesNumber }
                              )
                            )}
                          </span>

                          {orderFuture[0] === 'pod' ? (
                            orderFuture[1] === 'asc' ? (
                              <IconWrapper
                                iconName="chevron-up"
                                className={`${styles.Table__TH__Icon}`}
                              />
                            ) : (
                              <IconWrapper
                                iconName="chevron-down"
                                className={`${styles.Table__TH__Icon}`}
                              />
                            )
                          ) : (
                            <div style={{ height: 32, width: 19 }}></div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableFooter>
                ) : null}
              </Table>
            </TableContainer>
            </Collapsible>
          </div>
        ) : (
          <></>
        )
        //   <div
        //   className={`${styles.Table} pb-3`}
        //   key={uuidv4()}
        // >
        //   <div className={`${styles.Table__Title}`}>
        //     {futureColumns.title}
        //   </div>
        //   { dataStore?.data?.members?.future?.lenght === 0 && <div> {futureColumns.no_data_text}</div>}
        // </div>
      }

      {
        currentRows && currentRows?.length > 0 ? (
          <div
            ref={currentSectionRef}
            className={`${styles.Table} ${styles.Table__Members} mb-3`}
            key="current-members-section"
          >
            <div ref={currentTitlePlaceholderRef} />
            {currentColumns.title && (
              <div
                ref={currentTitleRef}
                className={`${styles.Table__Members__CollapseHeader} ${!allowCollapse ? styles.Table__Members__CollapseHeader__Static : ''}`}
                onClick={() => allowCollapse && setCurrentExpanded((prev) => !prev)}
              >
                <span>{currentColumns.title}</span>
                {allowCollapse && (
                  <IconWrapper
                    iconName="chevron-down"
                    className={`${styles.Table__Members__Chevron} ${currentExpanded ? styles.Table__Members__Chevron__Collapsed : ''}`}
                  />
                )}
              </div>
            )}

            <Collapsible
              open={!allowCollapse || currentExpanded}
              className={styles.Table__Members__CollapsibleOverride}
              outerRef={currentViewportRef}
            >
            <div ref={currentHeaderPlaceholderRef} />
            <div
              ref={currentHeaderRef}
              className={styles.Table__Members__StickyHeaderWrapper}
            >
              <TableContainer component={Paper} sx={{ overflow: 'visible' }}>
                <Table aria-label="current members header">
                  {renderMembersColGroup()}
                  <TableHead>
                    <TableRow>
                      {currentColumns &&
                        currentColumns.rows.map(
                          (column: any, column_i: number, column_arr: any) => (
                            <TableCell
                              {...(column_arr?.length - 1 === column_i
                                ? { align: 'right', dir: 'rtl' }
                                : {})}
                              className={`
                          ${
                            column.sortable
                              ? styles.Table__Header__Cell__Sortable
                              : styles.Table__Header__Cell__Static
                          }
                    ${
                      column.sortable && orderCurrent[0] === column.field
                        ? styles.Table__Header__Cell__Active
                        : styles.Table__Header__Cell
                    }`}
                              key={`current_members_head_col_${column_i}_${column.field}`}
                            >
                              <TableSortLabel
                                active={
                                  column.sortable &&
                                  orderCurrent[0] === column.field
                                }
                                direction={
                                  orderCurrent[0] === column.field &&
                                  orderCurrent[1] === 'asc'
                                    ? 'asc'
                                    : 'desc'
                                }
                                onClick={() =>
                                  column.sortable
                                    ? sortCurrentBy(column.field)
                                    : null
                                }
                                className={`${styles.Table__Header__Cell}`}
                                IconComponent={() =>
                                  orderCurrent[0] === column.field ? (
                                    orderCurrent[1] === 'asc' ? (
                                      <IconWrapper
                                        iconName="chevron-up"
                                        className={`${styles.Table__TH__Icon}`}
                                      />
                                    ) : (
                                      <IconWrapper
                                        iconName="chevron-down"
                                        className={`${styles.Table__TH__Icon}`}
                                      />
                                    )
                                  ) : (
                                    <div style={{ height: 32, width: 19 }}></div>
                                  )
                                }
                              >
                                <div> {column.title}</div>
                              </TableSortLabel>
                            </TableCell>
                          )
                        )}
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            </div>
            <TableContainer
              ref={currentBodyContainerRef}
              key="current-table-container"
              component={Paper}
              className={`${currentColumns.title ? 'mt-1' : ''}`}
              sx={{ overflow: 'visible' }}
            >
              <Table aria-label="simple table">
                {renderMembersColGroup()}
                <TableBody key="current-table-body">
                  {(currentRows || []).map((row) => (
                    <TableRow
                      key={`current_member_row_${row.memberId}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      className={`${styles.Table__Body__Row__Clickable}`}
                      style={{ height: 100 }}
                      onClick={() =>
                        (window.getSelection()?.toString() || '')?.length < 4
                          ? navigateToMember('member', row?.memberId, getMemberNavigationQueryParams(row?.memberId))
                          : null
                      }
                    >
                      <TableCell
                        className={`${
                          !futureColumns?.rows?.[0]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`current_cell_name_${row.memberId}`}
                        component="th"
                        scope="row"
                      >
                        <div className={`${styles.Table__Cell__Member__Name}`}>
                          <span>
                            {capitalizeFirstLetter(
                              ellipsizeMiddle(
                                `${row?.memberFirstName || ''} ${
                                  row?.memberLastName || ''
                                }`,
                                [
                                  30, 20, 20
                                ]
                              )
                            )}
                          </span>
                          <label className={styles.Table__Cell__Member__ID}>
                            {row?.memberExternalId || 'missing_member_id'}{' '}
                          </label>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !currentColumns?.rows?.[1]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`current_cell_pod_${row.memberId}`}
                        component="th"
                        scope="row"
                      >
                        <div>
                          <label
                            className={styles.Table__Cell__Member__Site__Label}
                          >
                            {row?.sites?.[0]?.pod || 'missing_pod'}
                            {/* {ellipsizeMiddle(

                            )}{' '} */}
                          </label>

                          <div
                            className={styles.Table__Cell__Member__Sites__Names}
                          >
                            {capitalizeFirstLetter(
                              row?.sites?.[0]?.siteName || 'missing name'
                            )}
                            <span
                              id={`current__popper__${
                                row?.sites?.[0]?.siteId || 'missing_id'
                              }`}
                              className={styles.Popper__Label}
                              onMouseEnter={(event: any) =>
                                handleClick(
                                  event,
                                  row?.sites?.[0]?.siteId || 'missing_id',
                                  'current'
                                )
                              }
                            >
                              {' '}
                              {row.sites.length > 1 &&
                                ` + ${row.sites.length - 1}`}
                            </span>
                            {currentOpen ===
                              (row?.sites?.[0]?.siteId || 'missing_id') &&
                              (typeof document !== 'undefined'
                                ? createPortal(
                                    <div
                                      ref={currentAnchorEl}
                                      className={`${styles.Popper__Wrapper}`}
                                      onMouseLeave={() => setCurrentOpen(null)}
                                      key={`current_popper_${row.memberId}`}
                                      style={
                                        currentPopperStyle ||
                                        getPopperFixedStyle(
                                          `current__popper__${
                                            row?.sites?.[0]?.siteId ||
                                            'missing_id'
                                          }`
                                        )
                                      }
                                    >
                                      {row.sites.map((memberSite: any) => {
                                        return (
                                          <div
                                            key={`current_popper_site_${memberSite.siteId}`}
                                            className={`${styles.Popper__Wrapper__Item}`}
                                            onClick={(ev: any) => (
                                              ev.preventDefault(),
                                              ev.stopPropagation(),
                                              navigateToSite(
                                                'site',
                                                memberSite?.siteId
                                              )
                                            )}
                                          >
                                            <p
                                              className={`${styles.Popper__Name}`}
                                            >
                                              {capitalizeFirstLetter(
                                                memberSite.siteName
                                              )}
                                            </p>
                                            <p
                                              className={`${styles.Popper__Text}`}
                                            >
                                              {memberSite.pod}
                                            </p>
                                          </div>
                                        );
                                      })}
                                    </div>,
                                    document.body
                                  )
                                : null)}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell
                        align="right"
                        className={`${
                          !currentColumns?.rows?.[2]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`current_cell_date_${row.memberId}`}
                        component="th"
                        scope="row"
                      >
                        <div>
                          <label style={{ alignSelf: 'flex-end' }}>
                            {formatHumanDate(row?.sites?.[0]?.startDate || '')}
                          </label>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {!hasCurrentStickyFooterHost ? (
                <TableFooter>
                  <TableRow>
                    <TableCell
                      align="left"
                      className={`${
                        currentColumns.rows[0].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          currentColumns.rows[0].sortable
                            ? sortCurrentBy('name')
                            : null
                        }
                        className={`${
                          currentColumns.rows[0].sortable &&
                          orderCurrent[0] === 'name'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        <span>
                          {parse(
                            t(
                              `widgets.tables.utils.${
                                (currentRows?.length || 0) === 1
                                  ? 'member'
                                  : 'members'
                              }`,
                              { members: currentRows?.length || 0 }
                            )
                          )}
                        </span>

                        {orderCurrent[0] === 'name' ? (
                          orderCurrent[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      className={`${
                        currentColumns.rows[0].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          currentColumns.rows[0].sortable
                            ? sortCurrentBy('pod')
                            : null
                        }
                        className={`${
                          currentColumns.rows[0].sortable &&
                          orderCurrent[0] === 'pod'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        <span>
                          {parse(
                            t(
                              `widgets.tables.utils.${
                                currentRowsSitesNumber === 1 ? 'site' : 'sites'
                              }`,
                              { sites: currentRowsSitesNumber }
                            )
                          )}
                        </span>

                        {orderCurrent[0] === 'pod' ? (
                          orderCurrent[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell align="left"></TableCell>
                  </TableRow>
                </TableFooter>
                ) : null}
              </Table>
            </TableContainer>
            </Collapsible>
          </div>
        ) : (
          <></>
        )
        //   <div
        //   className={`${styles.Table} pb-3`}
        //   key={uuidv4()}
        // >
        //   <div className={`${styles.Table__Title}`}>
        //     {currentColumns.title}
        //   </div>
        //   { dataStore?.data?.members?.current?.lenght === 0 && <div> {currentColumns.no_data_text}</div>}
        // </div>
      }

      {
        previousRows && previousRows?.length > 0 ? (
          <div
            ref={previousSectionRef}
            className={`${styles.Table} ${styles.Table__Members} mb-3`}
            key="previous-members-section"
          >
            <div ref={previousTitlePlaceholderRef} />
            {previousColumns.title && (
              <div
                ref={previousTitleRef}
                className={`${styles.Table__Members__CollapseHeader} ${!allowCollapse ? styles.Table__Members__CollapseHeader__Static : ''}`}
                onClick={() => allowCollapse && setPreviousExpanded((prev) => !prev)}
              >
                <span>{previousColumns.title}</span>
                {allowCollapse && (
                  <IconWrapper
                    iconName="chevron-down"
                    className={`${styles.Table__Members__Chevron} ${previousExpanded ? styles.Table__Members__Chevron__Collapsed : ''}`}
                  />
                )}
              </div>
            )}

            <Collapsible
              open={!allowCollapse || previousExpanded}
              className={styles.Table__Members__CollapsibleOverride}
              outerRef={previousViewportRef}
            >
            <div ref={previousHeaderPlaceholderRef} />
            <div
              ref={previousHeaderRef}
              className={styles.Table__Members__StickyHeaderWrapper}
            >
              <TableContainer component={Paper} sx={{ overflow: 'visible' }}>
                <Table aria-label="previous members header">
                  {renderMembersColGroup()}
                  <TableHead>
                    <TableRow>
                      {previousColumns &&
                        previousColumns.rows.map(
                          (column: any, column_i: number, column_arr: any) => (
                            <TableCell
                              {...(column_arr?.length - 1 === column_i
                                ? { align: 'right' }
                                : {})}
                              className={`
                        ${
                          column.sortable
                            ? styles.Table__Header__Cell__Sortable
                            : styles.Table__Header__Cell__Static
                        }
                      ${
                        column.sortable && orderPrevious[0] === column.field
                          ? styles.Table__Header__Cell__Active
                          : styles.Table__Header__Cell
                      }`}
                              key={`previous_members_head_col_${column_i}_${column.field}`}
                            >
                              <TableSortLabel
                                active={
                                  column.sortable &&
                                  orderPrevious[0] === column.field
                                }
                                direction={
                                  orderPrevious[0] === column.field &&
                                  orderPrevious[1] === 'asc'
                                    ? 'asc'
                                    : 'desc'
                                }
                                onClick={() =>
                                  column.sortable
                                    ? sortPreviousBy(column.field)
                                    : null
                                }
                                className={`${styles.Table__Header__Cell} `}
                                IconComponent={() =>
                                  orderPrevious[0] === column.field ? (
                                    orderPrevious[1] === 'asc' ? (
                                      <IconWrapper
                                        iconName="chevron-up"
                                        className={`${styles.Table__TH__Icon}`}
                                      />
                                    ) : (
                                      <IconWrapper
                                        iconName="chevron-down"
                                        className={`${styles.Table__TH__Icon}`}
                                      />
                                    )
                                  ) : (
                                    <></>
                                  )
                                }
                              >
                                <div> {column.title}</div>
                              </TableSortLabel>
                            </TableCell>
                          )
                        )}
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            </div>
            <TableContainer
              ref={previousBodyContainerRef}
              key="previous-table-container"
              component={Paper}
              className={`${previousColumns.title ? 'mt-1' : ''}`}
              sx={{ overflow: 'visible' }}
            >
              <Table aria-label="simple table">
                {renderMembersColGroup()}
                <TableBody key="previous-table-body">
                  {(previousRows || []).map((row) => (
                    <TableRow
                      key={`previous_member_row_${row.memberId}`}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      className={`${styles.Table__Body__Row__Clickable}`}
                      style={{ height: 100 }}
                      onClick={() =>
                        (window.getSelection()?.toString() || '')?.length < 4
                          ? navigateToMember('member', row?.memberId, getMemberNavigationQueryParams(row?.memberId))
                          : null
                      }
                    >
                      <TableCell
                        className={`${
                          !previousColumns?.rows?.[0]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`previous_cell_name_${row.memberId}`}
                        component="th"
                        scope="row"
                      >
                        <div className={`${styles.Table__Cell__Member__Name}`}>
                          <span>
                            {capitalizeFirstLetter(
                              ellipsizeMiddle(
                                `${row?.memberFirstName || ''} ${
                                  row?.memberLastName || ''
                                }`,
                                [
                                  30, 20, 20
                                ]
                              )
                            )}
                          </span>
                          <label className={styles.Table__Cell__Member__ID}>
                            {row?.memberExternalId || 'missing_member_id'}{' '}
                          </label>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !previousColumns?.rows?.[1]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`previous_cell_pod_${row.memberId}`}
                        component="th"
                        scope="row"
                      >
                        <div>
                          <label
                            className={styles.Table__Cell__Member__Site__Label}
                          >
                            {row?.sites?.[0]?.pod || 'missing_pod'}
                            {/* {ellipsizeMiddle(
                              row?.sites?.[0]?.pod || 'missing_pod'
                            )}{' '} */}
                          </label>

                          <div
                            className={styles.Table__Cell__Member__Sites__Names}
                          >
                            {capitalizeFirstLetter(
                              row?.sites?.[0]?.siteName || 'missing name'
                            )}
                            <span
                              id={`previous__popper__${
                                row?.sites?.[0]?.siteId || 'missing_id'
                              }`}
                              className={styles.Popper__Label}
                              onMouseEnter={(event: any) =>
                                handleClick(
                                  event,
                                  row?.sites?.[0]?.siteId || 'missing_id',
                                  'previous'
                                )
                              }
                            >
                              {' '}
                              {row.sites.length > 1 &&
                                ` + ${row.sites.length - 1}`}
                            </span>
                            {previousOpen ===
                              (row?.sites?.[0]?.siteId || 'missing_id') &&
                              (typeof document !== 'undefined'
                                ? createPortal(
                                    <div
                                      ref={previousAnchorEl}
                                      className={`${styles.Popper__Wrapper}`}
                                      onMouseLeave={() => setPreviousOpen(null)}
                                      key={`previous_popper_${row.memberId}`}
                                      style={
                                        previousPopperStyle ||
                                        getPopperFixedStyle(
                                          `previous__popper__${
                                            row?.sites?.[0]?.siteId ||
                                            'missing_id'
                                          }`
                                        )
                                      }
                                    >
                                      {row.sites.map((memberSite: any) => {
                                        return (
                                          <div
                                            key={`previous_popper_site_${memberSite.siteId}`}
                                            className={`${styles.Popper__Wrapper__Item}`}
                                            onClick={(ev: any) => (
                                              ev.preventDefault(),
                                              ev.stopPropagation(),
                                              navigateToSite(
                                                'site',
                                                memberSite?.siteId
                                              )
                                            )}
                                          >
                                            <p
                                              className={`${styles.Popper__Name}`}
                                            >
                                              {capitalizeFirstLetter(
                                                memberSite.siteName
                                              )}
                                            </p>
                                            <p
                                              className={`${styles.Popper__Text}`}
                                            >
                                              {memberSite.pod}
                                            </p>
                                          </div>
                                        );
                                      })}
                                    </div>,
                                    document.body
                                  )
                                : null)}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell
                        className={`${
                          !previousColumns?.rows?.[2]?.sortable === true &&
                          styles.Table__Cell__Static
                        }`}
                        key={`previous_cell_date_${row.memberId}`}
                        component="th"
                        scope="row"
                        align="right"
                      >
                        <div>
                          <label style={{ alignSelf: 'flex-end' }}>
                            {formatHumanDate(row?.sites?.[0]?.startDate || '')}{' '}
                            - {formatHumanDate(row?.sites?.[0]?.endDate || '')}
                          </label>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {!hasPreviousStickyFooterHost ? (
                <TableFooter>
                  <TableRow>
                    <TableCell
                      align="left"
                      className={`${
                        previousColumns.rows[0].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          previousColumns.rows[0].sortable
                            ? sortFutureBy('name')
                            : null
                        }
                        className={`${
                          previousColumns.rows[0].sortable &&
                          orderPrevious[0] === 'name'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        <span>
                          {parse(
                            t(
                              `widgets.tables.utils.${
                                (previousRows?.length || 0) === 1
                                  ? 'member'
                                  : 'members'
                              }`,
                              { members: previousRows?.length || 0 }
                            )
                          )}
                        </span>

                        {orderPrevious[0] === 'name' ? (
                          orderPrevious[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      className={`${
                        previousColumns.rows[0].sortable &&
                        styles.Table__Footer__Cell__Clickable
                      }`}
                    >
                      <div
                        onClick={() =>
                          previousColumns.rows[0].sortable
                            ? sortPreviousBy('pod')
                            : null
                        }
                        className={`${
                          previousColumns.rows[0].sortable &&
                          orderPrevious[0] === 'pod'
                            ? styles.Table__Footer__Cell__Active
                            : styles.Table__Footer__Cell
                        }`}
                      >
                        <span>
                          {parse(
                            t(
                              `widgets.tables.utils.${
                                previousRowsSitesNumber === 1 ? 'site' : 'sites'
                              }`,
                              { sites: previousRowsSitesNumber }
                            )
                          )}
                        </span>

                        {orderPrevious[0] === 'pod' ? (
                          orderPrevious[1] === 'asc' ? (
                            <IconWrapper
                              iconName="chevron-up"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          ) : (
                            <IconWrapper
                              iconName="chevron-down"
                              className={`${styles.Table__TH__Icon}`}
                            />
                          )
                        ) : (
                          <div style={{ height: 32, width: 19 }}></div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell align="left"></TableCell>
                  </TableRow>
                </TableFooter>
                ) : null}
              </Table>
            </TableContainer>
            </Collapsible>
          </div>
        ) : (
          <></>
        )
        //   <div
        //   className={`${styles.Table} pb-3`}
        //   key={uuidv4()}
        //   >
        //     <div className={`${styles.Table__Title}`}>
        //     {previousColumns.title}
        //   </div>
        //   { dataStore?.data?.members?.previous?.lenght === 0 && <div> { previousColumns.no_data_text}</div>}
        // </div>
      }
    </div>
  );
}

export default MembersTable;
